import { Component, Inject, inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of, Observable, Subject, catchError, filter } from 'rxjs';
import { first, map, switchMap, takeUntil } from 'rxjs';
import { Product } from '../../models';
import { Availability } from '../../models/availability';
import { CustomerProductPrice } from '../../models/price';
import { AssortmentRecursiveExtended, AssortmentService } from '../../services/assortment.service';
import { AuthService } from '../../services/auth/auth.service';
import { AvailabilityService } from '../../services/availability/availability.service';
import { CloudPimPropertiesHelperService } from '../../services/cloud-pim-properties-helper/cloud-pim-properties-helper.service';
import { GungFlowService } from '../../services/gung-flow/gung-flow.service';
import { MetadataService } from '../../services/metadata/metadata.service';
import { PriceService } from '../../services/price/price.service';
import { ProductService } from '../../services/products/product.service';
import { User } from '../../state/auth/types';
import { GungFlow } from '../../state/flow/types';
import { GroupedDetails } from '../gung-accordion-pim-data-templates/gung-accordion-pim-data-templates.component';
import { GungDownloadUrl } from 'gung-common';
import { PriceConfigService } from '../../services/price-config/price-config.service';
import { NavigationConfig } from '../../models/navigation-config';

@Component({
  selector: 'lib-product-detail-v2',
  templateUrl: './product-detail-v2.component.html',
  styleUrls: ['./product-detail-v2.component.scss'],
  standalone: false
})
export class ProductDetailV2Component implements OnInit, OnDestroy {
  @Input()
  productId?: string;

  public noCache = false;
  public isAnonymous = true;
  public isSales = false;
  public product: Product;
  public concept: Product;
  public availability: Availability;
  public price: CustomerProductPrice;
  public user: User;
  public flow: GungFlow;
  public components: Product[];
  public hasPimPropertiesToShow = false;
  public hasPimGroupsToShow = false;
  selectedImageIndex = 0;
  extraProperties: GroupedDetails[] = [];
  visibleDocuments = [];
  model3dFiles: string[] = [];

  currentAssortment?: AssortmentRecursiveExtended;
  isStandardErp = false;

  protected unsubscribe: Subject<void> = new Subject();

  protected isOnlyDisplayFlow: boolean = false;

  protected route = inject(ActivatedRoute);
  protected productService = inject(ProductService);
  protected availabilityService = inject(AvailabilityService);
  protected authService = inject(AuthService);
  protected priceService = inject(PriceService);
  protected metadataService = inject(MetadataService);
  protected translateService = inject(TranslateService);
  protected assortmentService = inject(AssortmentService);
  protected cloudPimPropertyHelper = inject(CloudPimPropertiesHelperService);
  protected gungFlowService = inject(GungFlowService);
  protected priceConfigService = inject(PriceConfigService);

  currentLang = this.translateService.currentLang;

  constructor(
    @Optional()
    @Inject('environment')
    public environment: NavigationConfig
  ) {}

  ngOnInit(): void {
    const baseObservable = this.route.params.pipe(
      map(params => {
        return this.productId || (params.productId as string);
      }),
      switchMap(productId =>
        forkJoin({
          productId: of(productId),
          multistock: this.authService.getCurrentUser().pipe(
            first(),
            filter(user => !!user),
            map(user => user.managedMultistockIds[0])
          ),
          flow: this.gungFlowService.getSelectedFlow().pipe(first())
        })
      ),
      switchMap(({ productId, multistock, flow }) =>
        forkJoin({
          product: this.productService.getProduct(productId),
          availability:
            flow.requireAvailability || flow.useAvailabilities
              ? this.availabilityService.getAvailability(productId, multistock, this.noCache).pipe(
                  catchError((err, caught) => {
                    return of(null);
                  })
                )
              : of(null),
          price: this.priceService.getCurrentCustomerPrice(productId).pipe(first()),
          user: this.authService.getCurrentUser().pipe(first()),
          flow: of(flow)
        })
      )
    );
    this.handleObservable(baseObservable);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  protected handleObservable(observable: Observable<BaseDetailData>): void {
    observable.pipe(takeUntil(this.unsubscribe)).subscribe(({ product, availability, price, user, flow }) => {
      this.mapData(product, availability, price, user, flow);
      this.checkForPimProperties(product);
      this.filterDocuments(user);
    });
  }

  protected mapData(product, availability, price, user, flow) {
    this.product = product;
    if (product.extra.gme) {
      this.isStandardErp = true;
      this.mapStandardErpsProduct();
    }
    this.availability = availability;
    this.price = price;
    this.isAnonymous = user.roles.filter(role => role.toUpperCase() === 'ANONYMOUS').length > 0;
    this.isSales =
      user.roles.filter(role => role.toUpperCase() === 'ADMIN' || role.toUpperCase() === 'SALES').length > 0;
    this.flow = flow;
    if (this.product.extra.conceptId) {
      this.productService.getProduct(this.product.extra.conceptId).subscribe(concept => {
        this.concept = concept;
      });
    }
    if (this.flow.displayOnly) {
      this.isOnlyDisplayFlow = this.flow.displayOnly;
    }
  }

  /**
   * Filter documents to show - no default filter for now. Need to add property to cloud pim to filter on
   */
  protected filterDocuments(user: User): void {
    this.visibleDocuments = this.product?.extra?.documents || [];
    this.model3dFiles = this.visibleDocuments
      .filter(doc => doc.extra?.documentType === '3D')
      .map(doc => GungDownloadUrl(doc));

    if (!!this.hasPimGroupsToShow) {
      if (!this.visibleDocuments.length) {
        return;
      }

      const items = this.visibleDocuments.map(doc => ({
        title:
          this.metadataService.getMetadataValue('documentType', 'description', doc.extra.documentType) ||
          doc?.i18n?.[this.currentLang]?.description ||
          doc?.category ||
          'DOCUMENT',
        type: 'link',
        url: GungDownloadUrl(doc),
        value: doc?.s3Uri?.split('/')?.reverse()[0] || 'DOCUMENT'
      }));

      this.extraProperties.push({
        groupId: 'documents',
        groupName: 'DOCUMENTS',
        items,
        collapsed: true
      });
    }
  }

  public checkForPimProperties(product: Product) {
    this.hasPimPropertiesToShow = this.cloudPimPropertyHelper.hasAnyPropertiesToShow(product);

    if (!!this.hasPimPropertiesToShow) {
      this.hasPimGroupsToShow = this.cloudPimPropertyHelper.hasGroupsOnProductTemplates(product);
    }
  }

  mapStandardErpsProduct() {
    this.product.name = this.product.extra.gme.displayName;
  }
}

interface BaseDetailData {
  product: Product;
  price: CustomerProductPrice;
  availability: Availability;
  user: User;
  flow: GungFlow;
}
